import React from 'react';

import ArticleListingItem from '$partials/items/article-listing-item';
import { SimpleImage } from '$partials/media';
import { formatPublicationDate } from '$utilities/data';
import { Button } from '$partials/ui';
import { Carousel, CarouselWrapper } from '$partials/base/carousel';
import { SwiperSlide } from 'swiper/react';
import IconChevron from '$partials/icons/icon-chevron';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/articles.d.ts').ArticleListingGrid}
 */
const ArticleListingGrid = (props) => {
    return (
        <>
            <section className='module-article-carousel py-48'>
                <div className='layout-container layout-site-outer-padding flex items-baseline'>
                    {props.heading && (
                        <h3 className='type-style-2'>
                            {props.heading}
                        </h3>
                    )}
                    {props.listingLink && (
                        <div className='ml-auto mr-0 hidden sm:block'>
                            <a
                                className='inline-flex type-style-eyebrow items-center'
                                href={props.listingLink.url}>
                                {props.listingLink.title}
                                <span aria-hidden={true}>
                                    <IconChevron className='ml-10 -rotate-90' />
                                </span>
                            </a>
                        </div>
                    )}
                </div>
                <div className='layout-container layout-site-outer-padding'>
                    {props.items.edges?.length > 0 && (
                        <>
                            {props.items.edges?.[0]?.node && (
                                <div
                                    className='flex flex-col md:flex-row col-wrapper mt-24'>
                                    {/* Property Image */}
                                    {props.items.edges[0].node.listingImageXl && (
                                        <SimpleImage
                                            {...props.items.edges[0].node.listingImageXl}
                                            className='md:w-8/12 col-padding h-full object-cover transition-transform duration-300'/>
                                    )}

                                    {/* title and Tags */}
                                    <div className='flex flex-col col-padding md:w-4/12 md:gap-y-24 mt-16 md:mt-0'>
                                        {/* Details Wrap - Eyebrow and Date */}
                                        {(props.items.edges[0].node?.parentListingEyebrow || props.items.edges[0].node?.publicationDate) && (
                                            <div className='flex gap-8 items-center type-style-eyebrow w-full'>
                                                {props.items.edges[0].node?.parentListingEyebrow && (
                                                    <p className='text-main-brand-text'>
                                                        {props.items.edges[0].node.parentListingEyebrow}
                                                    </p>
                                                )}
                                                {props.items.edges[0].node?.parentListingEyebrow && props.items.edges[0].node?.publicationDate && (
                                                    <p className='text-main-text-primary'>
                                                                                    /
                                                    </p>
                                                )}
                                                {props.items.edges[0].node?.publicationDate && (
                                                    <p className='text-main-text-primary'>
                                                        {formatPublicationDate(props.items.edges[0].node.publicationDate)}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {props.items.edges[0].node.title && (
                                            <a
                                                className='type-style-3 text-main-text-primary whitespace-pre-wrap'
                                                href={props.items.edges[0].node.url}>
                                                {props.items.edges[0].node.title}
                                            </a>
                                        )}

                                        {props.items.edges[0].node.listingText && (
                                            <p
                                                className='type-style-body-r text-main-text-primary whitespace-pre-wrap'>
                                                {props.items.edges[0].node.listingText}
                                            </p>
                                        )}

                                        {props.items.edges[0].node?.topics?.length > 0 && (
                                            <div className='flex flex-wrap gap-6 w-full'>
                                                {props.items.edges[0].node.topics.map((topic, index) => (
                                                    <Button
                                                        as='a'
                                                        className='button-tag'
                                                        href={`/topics/${topic.slug}`}
                                                        key={index}>
                                                        <p className='type-style-eyebrow text-tag-main-text'>
                                                            {topic.title}
                                                        </p>
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className='sm:-mx-16 md:-mx-24 mt-32 md:mt-48'>
                                {/* SM/MD listing */}
                                <div className='hidden sm:grid sm:grid-cols-2 md:grid-cols-3 gap-y-40'>
                                    {props.items.edges.map((edge, i) => {
                                        // First is featured above
                                        // Items capped at 4
                                        if (i === 0 || i > 3) {
                                            return null;
                                        }
                                        return (
                                            <>
                                                <article
                                                    className={`sm:px-16 md:px-24 sm:border-l sm:border-l-tint-dark ${(i-1) % 3 === 0 ? 'md:border-opacity-0' : 'md:border-opacity-20'} ${i % 2 !== 0 ? 'sm:border-opacity-0' : 'sm:border-opacity-20'}`}
                                                    key={edge.node.id}>
                                                    <ArticleListingItem {...edge.node}/>
                                                </article>
                                            </>
                                        );
                                    })}
                                </div>
                                {/* XS-only listing carousel */}
                                <div className='sm:hidden'>
                                    <Carousel swiperConfig={{
                                        slidesPerView: 'auto',
                                        spaceBetween: 0
                                    }}>
                                        <CarouselWrapper>
                                            {props.items.edges.map((edge, i) => {
                                                // First is featured above
                                                // Items capped at 4
                                                if (i === 0 || i > 3) {
                                                    return null;
                                                }
                                                const isLast = props.items.edges?.length > 4 ? i === 3 : props.items.edges?.length - 1 === i;
                                                return (
                                                    <>
                                                        <SwiperSlide
                                                            className='w-10/12 sm:w-5/12 md:w-4/12 flex'
                                                            key={edge.node.id || i}>
                                                            <ArticleListingItem {...edge.node} />
                                                            {(!isLast) && (
                                                                <div
                                                                    aria-hidden={true}
                                                                    className='mx-16 h-full border-l border-l-tint-dark/20 w-1'>
                                                                </div>
                                                            )}
                                                        </SwiperSlide>
                                                    </>
                                                );
                                            })}
                                        </CarouselWrapper>
                                    </Carousel>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default ArticleListingGrid;
